import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CoreMenuItem } from '@core/types';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { CompanygroupSettingsModel } from './models/companygroupSettings.model';
import { TradeUnionModel } from './models/corporate/tradeUnion.model';
import { DomainModel } from './models/domain.model';
import { FeatureFlagModel } from './models/featureFlag.model';
import { StreetTypeModel } from './models/hiring/streetType.model';
import { OptionModel } from './models/model';
import { QueryParams } from './models/odata/queryParams';
import { Response } from './models/response';
import { ServiceBase } from './servicebase';
import { CompanyGroupEmailSettingModel } from './models/companyGroupEmailSetting.model';
import { TimeOccurrenceParametersModel } from './models/timeOccurrenceParameters.model';

@Injectable()
export class DomainService extends ServiceBase<DomainModel> {
	domainSubject = new BehaviorSubject(null);

	constructor(http: HttpService) {
		super('companygroup', http);
	}

	createEntity(input: any): DomainModel {
		return DomainModel.create(input);
	}

	setDomain(domain: DomainModel) {
		if (domain) {
			this.domainSubject.next(domain);
		}
	}

	getDomain(): DomainModel {
		return this.domainSubject.value;
	}

	getMenu(): CoreMenuItem[] {
		let menus = this.getDomain()?.menu;

		if (menus) {
			const defaultMenu = {
				icon: 'feather-home',
				id: 'home',
				title: 'Inicio',
				type: 'item',
				url: `${this.getDomain().domainName}/home`,
			} as CoreMenuItem;

			return [defaultMenu, ...menus];
		}

		return [];
	}

	getAllowedUrls(): string[] {
		return this.getDomain()?.allowedUrls.concat([
			`${this.getDomain().domainName}/actionrequired`,
			`${this.getDomain().domainName}/outsourced`,
			`${this.getDomain().domainName}/hiring/workasset`,
		]);
	}

	get CompanyImageUrl(): string {
		const domain = this.getDomain();

		if (domain && domain?.image) {
			try {
				return domain.image;
			} catch (_) {
				return `${this.BASE_COMPANIES_IMAGE_URL}/${domain.image}`;
			}
		}

		return null;
	}

	checkDomainName(domainName: string, noCache: boolean = false, bypassLoading: boolean = false): Observable<DomainModel> {
		const options = noCache
			? {
				headers: new HttpHeaders({
					'Cache-Control': 'no-cache',
				}),
			}
			: undefined;
		return this.http.get(`${this.endpoint}/check.domainname/${domainName}`, options, null, null, bypassLoading).pipe(
			map(({ body }) => {
				return DomainModel.create(body);
			})
		);
	}

	checkModule(module: string): boolean {
		const modules = [...this.getMenu().filter(f => !f.children)];
		this.getMenu().forEach(item => {
			if (item.children) {
				modules.push(...item.children);
			}
		});
		const isModuleFound = modules.find(f => f.id === module) !== undefined;

		return isModuleFound;
	}

	getTradeUnions(params: QueryParams, disabledLoading: boolean = false): Observable<Response<TradeUnionModel[]>> {
		const query = this.createBuildQuery(params);
		return this.http.get(`companygroup/trade-unions/list${query}`, null, null, null, disabledLoading).pipe(
			map(res => {
				const response = new Response<TradeUnionModel[]>();
				response.data = res.body.map(pp => TradeUnionModel.create(pp));
				response.count = res.headers.get('x-count');
				return response;
			})
		);
	}

	get featureFlag(): FeatureFlagModel {
		return FeatureFlagModel.create(this.getDomain().featureFlag);
	}

	get timeOccurrenceParameters(): TimeOccurrenceParametersModel {
		return TimeOccurrenceParametersModel.create(this.getDomain().timeOccurrenceParameters);
	}

	getCompanyGroupSettingsWeb(): Observable<CompanygroupSettingsModel> {
		return this.http
			.get('companygroup/settings-web')
			.pipe(map((res: any) => CompanygroupSettingsModel.create(res.body)));
	}

	getCompanyGroupSettings(): Observable<CompanygroupSettingsModel> {
		return this.http
			.get('companygroup/settings')
			.pipe(map((res: any) => CompanygroupSettingsModel.create(res.body)));
	}

	saveSettings(settings: any): Observable<Response<any>> {
		return this.http.put(`${this.endpoint}/settings`, settings).pipe(
			map(res => {
				const response = new Response<any>();
				response.data = res;
				return response;
			})
		);
	}

	saveWorkforcePlanParameters(workforcePlanParameters: string): Observable<Response<any>> {
		return this.http
			.put(`${this.endpoint}/settings/workforce-plan-parameters`, { workforcePlanParameters })
			.pipe(
				map(res => {
					const response = new Response<any>();
					response.data = res;
					return response;
				})
			);
	}

	getStreetTypeOptions(disabledLoading: boolean = false): Observable<Response<OptionModel[]>> {
		return this.http.get('companygroup/list-street-types', null, null, null, disabledLoading).pipe(
			map((res: any) => {
				const response = new Response<OptionModel[]>();
				response.data = res.body.map((st: StreetTypeModel) =>
					StreetTypeModel.create(st).toOption()
				);
				return response;
			})
		);
	}

	public createOrUpdateEmailSetting(data: any): Observable<any> {
		if (data?.id) {
			return this.http.put(`${this.endpoint}/settings/email`, data, true);
		}

		return this.http.post(`${this.endpoint}/settings/email`, data, null, null, true);
	}

	public getEmailSetting(): Observable<CompanyGroupEmailSettingModel> {
		return this.http
			.get(`${this.endpoint}/settings/email`, null, null, null, true)
			.pipe(map((res: any) => (res.body ? CompanyGroupEmailSettingModel.create(res.body) : null)));
	}

	public validateEmailSettingCode(code: string): Observable<any> {
		return this.http.post(
			`${this.endpoint}/settings/email/validate-code/${code}`,
			{},
			null,
			null,
			true
		);
	}

	public resendEmailSetting(): Observable<any> {
		return this.http.post(`${this.endpoint}/settings/email/resend-code`, {}, null, null, true);
	}
}
